/**
 * API Configuration for ARGUS Platform
 * Uses Django Backend Only
 */

// Environment detection
const isProduction = process.env.NODE_ENV === 'production';

// Application identification (OLYMP or HERMES)
export const APP_NAME = process.env.REACT_APP_NAME || 'OLYMP';

// API URL Configuration - Django Backend Only
const API_BASE_URL_DJANGO = process.env.REACT_APP_API_URL
  || (isProduction ? '/api' : 'http://localhost:8000/api');

// Always use Django API
export const API_BASE_URL = API_BASE_URL_DJANGO;
export const useDjangoAPI = true;

// API Endpoints Configuration - Django Only
export const API_ENDPOINTS = {
  // Authentication (ARGUS Auth Service - JWT)
  AUTH: {
    LOGIN: '/auth/login/',
    REFRESH: '/auth/refresh/',
    VALIDATE_TOKEN: '/auth/verify/',
    LOGOUT: '/auth/logout/'
  },

  // ===== HERMES ENDPOINTS (via /api/hermes/) =====

  // Provider Requests (Provider perspective - all incoming requests)
  PROVIDER_REQUESTS: {
    LIST: '/hermes/provider/inbox/',
    DETAIL: '/hermes/provider/inbox/{id}/',
    UPDATE: '/hermes/provider/inbox/{id}/'
  },

  // Client Orders (Client perspective - own orders)
  CLIENT_ORDERS: {
    LIST: '/hermes/client/orders/',
    DETAIL: '/hermes/client/orders/{id}/',
    CREATE: '/hermes/client/orders/',
    UPDATE: '/hermes/client/orders/{id}/',
    STATS: '/hermes/client/dashboard/stats/',
    RECENT: '/hermes/client/orders/recent/',
    CHART_DATA: '/hermes/client/dashboard/charts/'
  },

  // Legacy REQUESTS endpoint for backward compatibility
  REQUESTS: {
    LIST: '/hermes/provider/inbox/',
    DETAIL: '/hermes/provider/inbox/{id}/',
    CREATE: '/hermes/client/orders/',
    UPDATE: '/hermes/provider/inbox/{id}/',
    STATS: '/hermes/client/dashboard/stats/',
    RECENT: '/hermes/client/orders/recent/',
    CHART_DATA: '/hermes/client/dashboard/charts/'
  },

  // Provider Dashboard (Provider perspective - all requests statistics)
  PROVIDER_DASHBOARD: {
    STATS: '/hermes/provider/statistics/',
    CHARTS: '/hermes/provider/statistics/',
    RECENT: '/hermes/provider/statistics/'
  },

  // Client Dashboard (Client perspective - own orders statistics)
  CLIENT_DASHBOARD: {
    STATS: '/hermes/client/dashboard/stats/',
    CHARTS: '/hermes/client/dashboard/charts/',
    RECENT: '/hermes/client/orders/recent/'
  },

  // Legacy DASHBOARD endpoint for backward compatibility
  DASHBOARD: {
    STATS: '/hermes/provider/statistics/',
    CHARTS: '/hermes/provider/statistics/'
  },

  // Feasibility
  FEASIBILITY: {
    CHECK: '/hermes/provider/orders/{id}/feasibility/',
    COMPLETE: '/hermes/provider/orders/{id}/complete-feasibility/'
  },

  // Negotiation
  NEGOTIATION: {
    GET: '/hermes/provider/operations/negotiations/{id}/',
    UPDATE: '/hermes/provider/operations/negotiations/{id}/',
    ADD_MESSAGE: '/hermes/provider/operations/negotiations/{id}/messages/',
    ACCEPT: '/hermes/provider/operations/negotiations/{id}/accept/',
    REJECT: '/hermes/provider/operations/negotiations/{id}/reject/'
  },

  // Order Execution
  EXECUTION: {
    GET_STATUS: '/hermes/provider/operations/execution/{id}/',
    START_STEP: '/hermes/provider/operations/execution/{id}/step/'
  },

  // ===== OLYMP-SPECIFIC ENDPOINTS =====

  // Data Catalog
  CATALOG: {
    LIST: '/olymp/catalog/',
    DETAIL: '/olymp/catalog/{id}/',
    SEARCH: '/olymp/catalog/search/',
    FAVORITES: '/olymp/catalog/favorites/',
    ADD_FAVORITE: '/olymp/catalog/{id}/favorite/',
    REMOVE_FAVORITE: '/olymp/catalog/{id}/unfavorite/',
    STATS: '/olymp/catalog/stats/',
    TAGS: '/olymp/catalog/tags/'
  },

  // OMOP Mapping
  MAPPING: {
    LIST: '/olymp/mapping/',
    DETAIL: '/olymp/mapping/{id}/',
    VALIDATE: '/olymp/mapping/{id}/validate/',
    SUGGEST: '/olymp/mapping/suggest/',
    STATS: '/olymp/mapping/stats/'
  },

  // Cart & Parameter Lists
  CART: {
    GET: '/olymp/cart/',
    ADD: '/olymp/cart/add/',
    REMOVE: '/olymp/cart/remove/{id}/',
    CLEAR: '/olymp/cart/clear/',
    EXPORT: '/olymp/cart/export/',
    SAVE_AS_LIST: '/olymp/cart/save-as-list/'
  },

  LISTS: {
    LIST: '/olymp/lists/',
    DETAIL: '/olymp/lists/{id}/',
    CREATE: '/olymp/lists/',
    UPDATE: '/olymp/lists/{id}/',
    DELETE: '/olymp/lists/{id}/',
    ADD_TO_CART: '/olymp/lists/{id}/add-to-cart/'
  },

  // Standard Sets
  SETS: {
    LIST: '/olymp/sets/',
    DETAIL: '/olymp/sets/{id}/',
    BY_CATEGORY: '/olymp/sets/category/{category}/',
    ADD_TO_CART: '/olymp/sets/{id}/add-to-cart/'
  },

  // Cohorts
  COHORTS: {
    LIST: '/olymp/cohorts/',
    DETAIL: '/olymp/cohorts/{id}/',
    CREATE: '/olymp/cohorts/',
    UPDATE: '/olymp/cohorts/{id}/',
    DELETE: '/olymp/cohorts/{id}/',
    EXPORT: '/olymp/cohorts/{id}/export/',
    FEASIBILITY: '/olymp/cohorts/{id}/feasibility/'
  },

  // Comments
  COMMENTS: {
    LIST: '/olymp/comments/',
    CREATE: '/olymp/comments/',
    UPDATE: '/olymp/comments/{id}/',
    DELETE: '/olymp/comments/{id}/',
    BY_PARAMETER: '/olymp/comments/parameter/{parameterId}/',
    BY_COHORT: '/olymp/comments/cohort/{cohortId}/'
  },

  // User Dashboard
  OLYMP_DASHBOARD: {
    STATS: '/olymp/dashboard/stats/',
    RECENT_ACTIVITY: '/olymp/dashboard/activity/',
    NOTIFICATIONS: '/olymp/dashboard/notifications/'
  }
};

// Helper function to build complete URLs
// Note: Returns only the endpoint path (without baseURL)
// Axios will automatically prepend the baseURL from API_CONFIG
export const buildApiUrl = (endpoint, params = {}) => {
  let url = endpoint; // Don't add baseURL here - axios will add it automatically

  // Replace path parameters (e.g., {id} with actual values)
  Object.keys(params).forEach(key => {
    url = url.replace(`{${key}}`, params[key]);
  });

  return url;
};

// Configuration object for axios
export const API_CONFIG = {
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  withCredentials: false
};

// Debug logging
console.log('=== API Configuration Debug ===');
console.log('Environment Variables:');
console.log('- NODE_ENV:', process.env.NODE_ENV);
console.log('- REACT_APP_API_URL:', process.env.REACT_APP_API_URL);
console.log('Computed Values:');
console.log('- isProduction:', isProduction);
console.log('- Backend: Django (Fixed)');
console.log('- API_BASE_URL:', API_BASE_URL);
console.log('- LOGIN_ENDPOINT:', API_ENDPOINTS.AUTH.LOGIN);
console.log('===============================');

export default {
  API_BASE_URL,
  API_ENDPOINTS,
  API_CONFIG,
  buildApiUrl
};